// JiraTicketDetailsModal.js
import React from "react";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Grid,
  LinearProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Link,
} from "@mui/material";
import { Close as CloseIcon, OpenInNew } from "@mui/icons-material";
import { IconMdiJira } from "../jira/jiraIcon";

const STATUS_COLORS = {
  "On Hold": "#F9CA00",
  "In Progress": "#0088FE",
  Done: "#70C930",
  Blocked: "#BA1650",
  Cancelled: "#777",
};

const ISSUE_TYPE_COLORS = {
  Story: "#70C930",
  Bug: "#BA1650",
  Task: "#0088FE",
  "Sub-task": "#F9CA00",
  Unknown: "#AAAAAA",
};

const JiraTicketDetailsModal = ({ open, onClose, ticket }) => {
  if (!ticket) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styles.container}>
        <Paper sx={styles.paper}>
          <Box sx={styles.header}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#fff" }}>
              Jira Ticket Details: {ticket.key}
            </Typography>
            <Box
              sx={{
                mt: 1,
                ml: 2,
                textAlign: "left",
                width: "fit-content",
                flex: 1,
              }}
            >
              <Link
                href={`https://notified-safe.atlassian.net/browse/${ticket.key}`}
                target="_blank"
                sx={{
                  color: "#F9CA00",
                  display: "inline-flex",
                  alignItems: "center",
                  textDecoration: "none",
                }}
              >
                <IconMdiJira fontSize="small" sx={{ ml: 1 }} />
              </Link>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Box>

          <Divider sx={{ bgcolor: "#444" }} />

          <Box>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 3, padding: 3 }}>
              <Grid item>
                <Avatar
                  src={ticket.assigneeAvatar}
                  sx={{ width: 72, height: 72, border: "2px solid #F9CA00" }}
                />
              </Grid>
              <Grid item xs>
                <Typography variant="h6" sx={{ color: "#fff" }}>
                  {ticket.assignee} (Assigned)
                </Typography>
                <Typography variant="body2" sx={{ color: "#aaa" }}>
                  {ticket.assigneeEmailAddress}
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  label={ticket.status}
                  sx={{
                    bgcolor: STATUS_COLORS[ticket.status] || "#555",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    height: 28,
                  }}
                />
              </Grid>
            </Grid>

            <Table size="small">
              <TableBody>
                {[
                  ["Summary", ticket.summary],
                  ["Issue Type", ticket.issueType],
                  ["Epic", ticket.epicName],
                  ["Sprint", ticket.sprintName],
                  ["Sprint Start", ticket.sprintStart],
                  ["Sprint End", ticket.sprintEnd],
                  ["Sprint State", ticket.sprintState],
                  ["Last Updated", ticket.updated],
                  [
                    "Created by",
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Avatar src={ticket.avatarUrl} sx={{ width: 32, height: 32 }} />
                      </Grid>
                      <Grid item>
                        <Typography variant="body2" sx={{ color: "#fff" }}>
                          {ticket.assignee}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#aaa" }}>
                          {ticket.assigneeEmailAddress}
                        </Typography>
                      </Grid>
                    </Grid>,
                  ],
                ].map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell
                      sx={{
                        color: "#F9CA00",
                        fontWeight: "bold",
                        borderBottom: "1px solid #444",
                      }}
                    >
                      {label}
                    </TableCell>
                    <TableCell sx={{ color: "#ddd", borderBottom: "1px solid #444" }}>
                      {label === "Issue Type" ? (
                        <Chip
                          variant="outlined"
                          label={value}
                          sx={{
                            borderColor: ISSUE_TYPE_COLORS[value] || "#777",
                            color: ISSUE_TYPE_COLORS[value] || "#777",
                            height: 22,
                            fontWeight: "bold",
                          }}
                        />
                      ) : (
                        value
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box sx={{ mt: 3, p: 3 }}>
              <Typography sx={{ color: "#ccc", mb: 1 }}>
                Progress: {ticket.percentComplete}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={ticket.percentComplete}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#555",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: ticket.percentComplete === 100 ? "#70C930" : "#F9CA00",
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    bgcolor: "rgba(0,0,0,0.7)",
    height: "100vh",
  },
  paper: {
    width: "90%",
    maxWidth: 650,
    bgcolor: "#2A2A2A",
    borderRadius: 3,
    color: "#fff",
    boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    p: 2,
    bgcolor: "#1F1F1F",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
};

export default JiraTicketDetailsModal;
